import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError, openPdf, printPdf, API } from "@/lib/api";
import { AuthImage } from "@/components/AuthImage";
import { ManufacturerSelect } from "@/components/ManufacturerSelect";
import { CatalogSelect } from "@/components/CatalogSelect";
import { InspectionsDialog } from "@/components/InspectionsDialog";
import { PageHeader, Label2 } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { STATUS_TONE, healthTone } from "@/pages/Standorte";
import { Heartbeat, Plus, PuzzlePiece, Barcode, CaretLeft, PencilSimple, WarningCircle, ClockCountdown, ClipboardText, CheckCircle, QrCode, Images } from "@phosphor-icons/react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";

const COMP_TONE = { ok: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600", wartung_faellig: "border-amber-500/40 bg-amber-500/10 text-amber-600", pruefung_unvollstaendig: "border-orange-500/40 bg-orange-500/10 text-orange-600", mangel: "border-amber-500/40 bg-amber-500/10 text-amber-600", sicherheitsmangel: "border-red-500/40 bg-red-500/10 text-red-600", ausser_betrieb: "border-red-500/40 bg-red-500/10 text-red-600" };
const RULE_TONE = { ok: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600", faellig: "border-amber-500/40 bg-amber-500/10 text-amber-600", ueberfaellig: "border-red-500/40 bg-red-500/10 text-red-600", unbekannt: "border-border bg-muted text-muted-foreground" };
const RULE_LABEL = { ok: "OK", faellig: "Fällig", ueberfaellig: "Überfällig", unbekannt: "Kein Datum" };
const DEFECT_TONE = { offen: "border-amber-500/40 bg-amber-500/10 text-amber-600", beauftragt: "border-blue-500/40 bg-blue-500/10 text-blue-600", behoben: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600", abgelehnt: "border-red-500/40 bg-red-500/10 text-red-600" };
const KLASSE_TONE = { geringfuegig: "text-emerald-600", erheblich: "text-amber-600", gefaehrlich: "text-red-600" };
const groupTypes = (types) => types.reduce((m, t) => { const k = t.kategorie || "Sonstiges"; (m[k] = m[k] || []).push(t); return m; }, {});
const emptyForm = { art_key: "", kind: "", bezeichnung: "", komponentennummer: "", manufacturer: "", modell: "", serial_no: "", baujahr: "", auftragsnummer: "", produktnummer: "", inventarnummer: "", standort_im_objekt: "", einbaudatum: "", inbetriebnahmedatum: "", betriebsstatus: "", beschreibung: "", bemerkungen: "", specs: {} };

const SONST = "__sonstiges__";

function OcrPanel({ artKey, kind, onApply, existingPhotos = [] }) {
  const [category, setCategory] = useState("Hauptschild");
  const [uploads, setUploads] = useState([]);
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [sel, setSel] = useState({});

  const handleFiles = async (files) => {
    setBusy(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", category);
        const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setUploads((u) => [...u, { media_id: data.id, name: file.name, category }]);
      }
    } catch (e) { toast.error("Upload fehlgeschlagen", { description: formatApiError(e.response?.data?.detail) }); }
    setBusy(false);
  };

  const extract = async () => {
    if (!uploads.length) return;
    setBusy(true); setSuggestions(null);
    try {
      const { data } = await api.post("/v1/components/ocr-extract", { media_ids: uploads.map((u) => u.media_id), art_key: artKey, typ_key: kind });
      setSuggestions(data.suggestions || []);
      const s = {}; (data.suggestions || []).forEach((x) => { s[x.key] = { checked: true, value: x.value }; }); setSel(s);
      if (!(data.suggestions || []).length) toast.info("Keine Werte erkannt");
    } catch (e) { toast.error("KI-Auslesung fehlgeschlagen", { description: formatApiError(e.response?.data?.detail) }); }
    setBusy(false);
  };

  const apply = () => {
    const chosen = (suggestions || []).filter((x) => sel[x.key]?.checked).map((x) => ({ ...x, value: sel[x.key].value }));
    onApply(chosen, uploads.map((u) => u.media_id));
    toast.success(`${chosen.length} Feld(er) übernommen · ${uploads.length} Foto(s) verknüpft`);
  };

  const confBadge = (c) => {
    if (c == null) return null;
    const pct = Math.round(c * 100);
    const tone = c >= 0.8 ? "border-emerald-500/40 text-emerald-600" : c >= 0.5 ? "border-amber-500/40 text-amber-600" : "border-red-500/40 text-red-600";
    return <Badge variant="outline" className={`rounded-md text-[10px] ${tone}`}>{pct}%</Badge>;
  };

  return (
    <div className="rounded-md border border-primary/30 bg-primary/5 p-3" data-testid="ocr-panel">
      <div className="flex items-center gap-2"><Barcode size={16} className="text-primary" /><Label2>Typenschild scannen (KI)</Label2></div>
      <p className="mt-0.5 text-xs text-muted-foreground">Foto(s) hochladen — die KI liest Hersteller, Modell, Seriennummer, Baujahr und passende technische Werte aus. Mehrere Schilder (Haupt/Antrieb/Steuerung) werden gemeinsam ausgewertet.</p>
      {existingPhotos.length > 0 && (
        <div className="mt-2" data-testid="ocr-existing-photos">
          <span className="text-[11px] text-muted-foreground">Hinterlegte Typenschilder:</span>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {existingPhotos.map((mid) => <AuthImage key={mid} mediaId={mid} alt="Typenschild" className="h-12 w-12 rounded-md border border-border object-cover" />)}
          </div>
        </div>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="h-9 w-44" data-testid="ocr-category"><SelectValue /></SelectTrigger>
          <SelectContent>{["Hauptschild", "Antriebsschild", "Steuerungsschild"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent" data-testid="ocr-upload-label">
          <Plus size={14} /> Foto(s) hinzufügen
          <input type="file" accept="image/*" multiple capture="environment" className="hidden" data-testid="ocr-file-input" onChange={(e) => handleFiles(e.target.files)} />
        </label>
        <Button type="button" size="sm" disabled={!uploads.length || busy} data-testid="ocr-extract-btn" onClick={extract}>{busy ? "…" : "Auslesen"}</Button>
      </div>
      {uploads.length > 0 && <div className="mt-2 flex flex-wrap gap-1">{uploads.map((u, i) => <Badge key={i} variant="outline" className="text-[10px]">{u.category}: {u.name}</Badge>)}</div>}
      {suggestions && suggestions.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="ocr-suggestions">
          <Label2>Vorschläge prüfen & übernehmen</Label2>
          {suggestions.map((x) => (
            <div key={x.key} data-testid={`ocr-sugg-${x.key}`} className="flex items-center gap-2 rounded-md border border-border bg-background px-2 py-1.5">
              <input type="checkbox" checked={!!sel[x.key]?.checked} data-testid={`ocr-check-${x.key}`} onChange={(e) => setSel((s) => ({ ...s, [x.key]: { ...s[x.key], checked: e.target.checked } }))} />
              <span className="w-40 shrink-0 text-xs text-muted-foreground">{x.label}</span>
              <Input className="h-8 flex-1" value={sel[x.key]?.value ?? ""} data-testid={`ocr-value-${x.key}`} onChange={(e) => setSel((s) => ({ ...s, [x.key]: { ...s[x.key], value: e.target.value } }))} />
              {confBadge(x.confidence)}
            </div>
          ))}
          <Button type="button" size="sm" data-testid="ocr-apply-btn" onClick={apply}>In Formular übernehmen</Button>
        </div>
      )}
    </div>
  );
}

function OptionSingle({ def, list, value, onChange }) {
  const items = (list.items || []).filter((i) => i.active !== false).slice().sort((a, b) => (a.sort || 0) - (b.sort || 0));
  const labels = items.map((i) => i.label);
  const allowSonst = def.allow_sonstiges || list.allow_sonstiges;
  const label = def.name + (def.required ? " *" : "");
  const grouped = items.some((i) => i.gruppe);
  const groups = grouped ? items.reduce((m, i) => { const g = i.gruppe || "Weitere"; (m[g] = m[g] || []).push(i); return m; }, {}) : null;
  const isCustom = value != null && value !== "" && !labels.includes(value);
  const [sonst, setSonst] = useState(isCustom);
  useEffect(() => { if (isCustom) setSonst(true); }, [isCustom]);
  const selectVal = sonst ? SONST : (labels.includes(value) ? value : "");
  const onSel = (v) => { if (v === SONST) { setSonst(true); onChange(""); } else { setSonst(false); onChange(v); } };
  return (
    <div><Label2>{label}</Label2>
      <Select value={selectVal} onValueChange={onSel}>
        <SelectTrigger data-testid={`spec-${def.key}`}><SelectValue placeholder="—" /></SelectTrigger>
        <SelectContent>
          {grouped
            ? Object.entries(groups).map(([g, its]) => (
                <SelectGroup key={g}><SelectLabel>{g}</SelectLabel>{its.map((it) => <SelectItem key={it.key} value={it.label}>{it.label}</SelectItem>)}</SelectGroup>))
            : items.map((it) => <SelectItem key={it.key} value={it.label}>{it.label}</SelectItem>)}
          {allowSonst && <SelectItem value={SONST}>Sonstiges …</SelectItem>}
        </SelectContent>
      </Select>
      {sonst && <Input className="mt-1.5" value={value ?? ""} data-testid={`spec-${def.key}-sonstiges`} placeholder="Bitte angeben …" onChange={(e) => onChange(e.target.value)} />}
    </div>
  );
}

function OptionMulti({ def, list, value, onChange }) {
  const items = (list.items || []).filter((i) => i.active !== false).slice().sort((a, b) => (a.sort || 0) - (b.sort || 0));
  const labels = items.map((i) => i.label);
  const allowSonst = def.allow_sonstiges || list.allow_sonstiges;
  const label = def.name + (def.required ? " *" : "");
  const arr = Array.isArray(value) ? value : [];
  const [custom, setCustom] = useState("");
  const toggle = (o) => { const s = new Set(arr); s.has(o) ? s.delete(o) : s.add(o); onChange([...s]); };
  const addCustom = () => { const v = custom.trim(); if (v && !arr.includes(v)) onChange([...arr, v]); setCustom(""); };
  return (
    <div className="sm:col-span-2"><Label2>{label}</Label2>
      <div className="mt-1 flex flex-wrap gap-1.5" data-testid={`spec-${def.key}`}>
        {items.map((it) => { const on = arr.includes(it.label); return (
          <button key={it.key} type="button" onClick={() => toggle(it.label)} className={`rounded-md border px-2 py-1 text-xs transition-colors ${on ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-accent"}`}>{it.label}</button>); })}
        {arr.filter((v) => !labels.includes(v)).map((v) => (
          <button key={v} type="button" onClick={() => toggle(v)} className="rounded-md border border-primary bg-primary/10 px-2 py-1 text-xs text-primary">{v} ✕</button>))}
      </div>
      {allowSonst && (
        <div className="mt-1.5 flex gap-1.5">
          <Input value={custom} data-testid={`spec-${def.key}-sonstiges`} placeholder="Sonstiges hinzufügen …" onChange={(e) => setCustom(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }} />
          <Button type="button" variant="outline" size="sm" onClick={addCustom}>Hinzufügen</Button>
        </div>
      )}
    </div>
  );
}

function SpecField({ def, value, onChange, optionLists }) {
  const label = (def.unit ? `${def.name} (${def.unit})` : def.name) + (def.required ? " *" : "");
  if (def.option_list_key && optionLists && optionLists[def.option_list_key]) {
    const list = optionLists[def.option_list_key];
    return def.field_type === "multiselect"
      ? <OptionMulti def={def} list={list} value={value} onChange={onChange} />
      : <OptionSingle def={def} list={list} value={value} onChange={onChange} />;
  }
  if (def.field_type === "bool") {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2">
        <span className="text-sm">{def.name}{def.required ? " *" : ""}</span>
        <Switch checked={!!value} onCheckedChange={onChange} data-testid={`spec-${def.key}`} />
      </div>
    );
  }
  if (def.field_type === "date") {
    return (<div><Label2>{label}</Label2><Input type="date" value={value || ""} data-testid={`spec-${def.key}`} onChange={(e) => onChange(e.target.value)} /></div>);
  }
  if (def.field_type === "doc_upload" || def.field_type === "image_upload") {
    return (<div><Label2>{label}</Label2><Input disabled placeholder="Upload in Phase 2 (Typenschilder)" data-testid={`spec-${def.key}`} /></div>);
  }
  if (def.field_type === "select") {
    return (
      <div><Label2>{label}</Label2>
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger data-testid={`spec-${def.key}`}><SelectValue placeholder="—" /></SelectTrigger>
          <SelectContent>{(def.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    );
  }
  if (def.field_type === "multiselect") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (o) => { const s = new Set(arr); s.has(o) ? s.delete(o) : s.add(o); onChange([...s]); };
    return (
      <div className="sm:col-span-2"><Label2>{label}</Label2>
        <div className="mt-1 flex flex-wrap gap-1.5" data-testid={`spec-${def.key}`}>
          {(def.options || []).map((o) => {
            const on = arr.includes(o);
            return <button key={o} type="button" onClick={() => toggle(o)} className={`rounded-md border px-2 py-1 text-xs transition-colors ${on ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-accent"}`}>{o}</button>;
          })}
        </div>
      </div>
    );
  }
  return (
    <div><Label2>{label}</Label2>
      <Input type={def.field_type === "number" ? "number" : "text"} value={value ?? ""} data-testid={`spec-${def.key}`}
        onChange={(e) => onChange(def.field_type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} />
    </div>
  );
}

export default function AnlageAkte() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [parent, setParent] = useState(null);
  const [comps, setComps] = useState(null);
  const [status, setStatus] = useState(null);
  const [compTypes, setCompTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [fieldDefs, setFieldDefs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [rules, setRules] = useState(null);
  const [history, setHistory] = useState(null);
  const [defects, setDefects] = useState(null);
  const [knowledge, setKnowledge] = useState(null);
  const [health, setHealth] = useState(null);
  const [logInput, setLogInput] = useState({});
  const [optionLists, setOptionLists] = useState({});
  const [qrComp, setQrComp] = useState(null);
  const [labelDlg, setLabelDlg] = useState({ open: false, path: "" });
  const [labelFmt, setLabelFmt] = useState("standard");
  const [labelFormats, setLabelFormats] = useState([]);
  const [galleryComp, setGalleryComp] = useState(null);
  const [galleryMedia, setGalleryMedia] = useState([]);
  const [galleryFilter, setGalleryFilter] = useState("Alle");
  const [lightbox, setLightbox] = useState(null);
  const [inspComp, setInspComp] = useState(null);
  const token = localStorage.getItem("csp_token");
  const openGallery = async (c) => {
    setGalleryComp(c); setGalleryFilter("Alle"); setGalleryMedia([]);
    try { const { data } = await api.get(`/v1/components/${c.id}/media`); setGalleryMedia(data || []); } catch { setGalleryMedia([]); }
  };

  const load = useCallback(() => {
    api.get(`/assets/${id}`).then((r) => {
      const a = r.data.asset || r.data;
      setAsset(a);
      if (a?.parent_id) api.get(`/assets/${a.parent_id}`).then((pr) => setParent(pr.data.asset || pr.data)).catch(() => {});
    }).catch(() => setAsset({ id, name: "Anlage" }));
    api.get(`/v1/assets/${id}/components`).then((r) => setComps(r.data));
    api.get(`/v1/assets/${id}/status`).then((r) => setStatus(r.data));
    api.get("/v1/catalogs/component-types").then((r) => setCompTypes(r.data));
    api.get("/v1/catalogs/component-categories").then((r) => setCategories(r.data)).catch(() => setCategories([]));
    api.get("/v1/catalogs/component-field-defs").then((r) => setFieldDefs(r.data)).catch(() => setFieldDefs([]));
    api.get("/v1/catalogs/component-option-lists").then((r) => setOptionLists((r.data || []).reduce((m, l) => { m[l.key] = l; return m; }, {}))).catch(() => setOptionLists({}));
    api.get("/v1/label-formats").then((r) => setLabelFormats(r.data || [])).catch(() => setLabelFormats([]));
    api.get(`/v1/assets/${id}/service-rules`).then((r) => setRules(r.data)).catch(() => setRules({ rules: [], warnings: [] }));
    api.get(`/v1/assets/${id}/maintenance-history`).then((r) => setHistory(r.data)).catch(() => setHistory([]));
    api.get(`/v1/assets/${id}/defects`).then((r) => setDefects(r.data)).catch(() => setDefects([]));
    api.get(`/v1/assets/${id}/twin-knowledge`).then((r) => setKnowledge(r.data)).catch(() => setKnowledge(null));
    api.get(`/v1/assets/${id}/health`).then((r) => setHealth(r.data)).catch(() => setHealth(null));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const saveLog = async (ruleKey) => {
    const val = logInput[ruleKey];
    if (!val) return;
    try { await api.put(`/v1/assets/${id}/service-log`, { rule_key: ruleKey, last_done: val }); toast.success("Durchführung erfasst"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openCreate = () => { setEditId(null); const art = categories[0]?.key || "sonstige"; const firstKind = compTypes.find((t) => t.art_key === art)?.key || compTypes[0]?.key || "sonstiges"; setForm({ ...emptyForm, art_key: art, kind: firstKind }); setOpen(true); };
  const openEdit = (c) => { setEditId(c.id); setForm({ ...emptyForm, ...c, specs: c.specs || {} }); setOpen(true); };

  const setArt = (art) => { const firstKind = compTypes.find((t) => t.art_key === art)?.key || ""; setForm((f) => ({ ...f, art_key: art, kind: firstKind })); };
  const setSpec = (key, val) => setForm((f) => ({ ...f, specs: { ...f.specs, [key]: val } }));
  const applyOcr = (chosen, mediaIds = []) => setForm((f) => {
    const nf = { ...f, specs: { ...f.specs } };
    chosen.forEach((x) => { if (x.is_spec) nf.specs[x.key] = x.value; else nf[x.key] = x.value; });
    nf.photos = Array.from(new Set([...(f.photos || []), ...mediaIds]));
    if (!nf.typenschild_media_id && mediaIds.length) nf.typenschild_media_id = mediaIds[0];
    return nf;
  });
  const relevantDefsFor = (art, kind) => fieldDefs.filter((d) => d.typ_key ? d.typ_key === kind : (d.art_key === "_all" || d.art_key === art));

  const save = async () => {
    if (!form.bezeichnung || !form.kind) return;
    const defs = relevantDefsFor(form.art_key, form.kind);
    const isEmpty = (v) => v === undefined || v === "" || v === null || (Array.isArray(v) && !v.length);
    const missing = defs.filter((d) => d.required && isEmpty(form.specs[d.key]));
    if (missing.length) { toast.error("Pflichtfelder fehlen", { description: missing.map((d) => d.name).join(", ") }); return; }
    try {
      if (editId) { await api.put(`/v1/components/${editId}`, form); toast.success("Komponente aktualisiert"); }
      else { await api.post(`/v1/assets/${id}/components`, form); toast.success("Komponente hinzugefügt"); }
      setOpen(false); setForm(emptyForm); setEditId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!comps || !status) return <div className="space-y-4"><Skeleton className="h-24 rounded-md" /><Skeleton className="h-48 rounded-md" /></div>;
  const typeGroups = groupTypes(compTypes);
  const typeName = (key) => compTypes.find((t) => t.key === key)?.name || key;
  const numLabel = categories.find((c) => c.key === form.art_key)?.nummer_label || "Komponentennummer";
  const SECTION_ORDER = ["Abmessungen", "Ausführung", "Antrieb & Steuerung", "Sicherheitsausstattung", "Klassifizierungen & Zulassungen", "Wartungsdaten"];
  const relevantDefs = relevantDefsFor(form.art_key, form.kind);
  const relevantKeys = new Set(relevantDefs.map((d) => d.key));
  const orphanEntries = Object.entries(form.specs || {}).filter(([k, v]) => !relevantKeys.has(k) && !["feder_links", "feder_rechts"].includes(k) && !(v === undefined || v === "" || v === null || (Array.isArray(v) && !v.length)));
  const activeGroups = relevantDefs.reduce((m, d) => { const g = d.gruppe || "Technische Daten"; (m[g] = m[g] || []).push(d); return m; }, {});
  const orderedGroups = Object.entries(activeGroups).sort((a, b) => (SECTION_ORDER.indexOf(a[0]) + 100) - (SECTION_ORDER.indexOf(b[0]) + 100));

  return (
    <div>
      <button onClick={() => navigate(-1)} className="mb-3 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><CaretLeft size={16} /> Zurück</button>
      <PageHeader title={asset?.name || "Anlagenakte"} subtitle={`${asset?.asset_type || "Anlage"} · QR ${asset?.qr_code || "—"}`}>
        {asset?.kundenbezeichnung && <Badge variant="outline" className="rounded-md" data-testid="anlage-kundenbez">Kunde: {asset.kundenbezeichnung}</Badge>}
        <Badge variant="outline" className={`rounded-md ${STATUS_TONE[status.status] || ""}`} data-testid="anlage-status">{status.status}</Badge>
        <Badge variant="outline" className={`gap-1 rounded-md ${healthTone(status.health_index)}`} data-testid="anlage-health"><Heartbeat size={13} weight="fill" />v1 {status.health_index}%</Badge>
        {health?.v2 && <Badge variant="outline" className={`gap-1 rounded-md ${healthTone(health.v2.health_index)}`} data-testid="anlage-health-v2"><Heartbeat size={13} weight="fill" />v2 {health.v2.health_index}%</Badge>}
      </PageHeader>

      {asset?.operational_status === "ausgefallen" && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-2.5 text-sm text-red-600" data-testid="anlage-blocked-banner">
          <WarningCircle size={18} weight="fill" /> <b>Anlage gesperrt (ausgefallen)</b> — automatisch durch Business Rule wegen gefährlichem sicherheitsrelevantem Mangel.
        </div>
      )}

      {parent && (
        <button onClick={() => navigate(`/anlage/${parent.id}`)} data-testid="parent-link"
          className="mb-4 inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-xs text-muted-foreground hover:bg-accent">
          Teil von: <span className="font-semibold text-foreground">{parent.name}</span>
        </button>
      )}

      <Card className="rounded-md border" data-testid="component-list">
        <div className="flex items-center justify-between border-b border-border p-4">
          <div className="flex items-center gap-2"><PuzzlePiece size={18} weight="duotone" className="text-primary" /><Label2>Komponenten ({status.components_total})</Label2></div>
          <div className="flex items-center gap-2">
            {comps.length > 0 && <Button size="sm" variant="ghost" className="gap-2" data-testid="qr-labels-pdf-btn" onClick={() => setLabelDlg({ open: true, path: `/v1/assets/${id}/qr-labels.pdf` })}><Barcode size={15} weight="bold" /> QR-Etiketten</Button>}
            <Button size="sm" variant="outline" className="gap-2" data-testid="add-component-btn" onClick={openCreate}><Plus size={15} weight="bold" /> Komponente</Button>
          </div>
        </div>
        <div className="divide-y divide-border">
          {comps.length === 0 && <div className="p-6 text-sm text-muted-foreground">Noch keine Komponenten.</div>}
          {comps.map((c) => (
            <div key={c.id} data-testid={`comp-${c.id}`} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-semibold">{c.bezeichnung}</span>
                  {c.is_hauptkomponente && <Badge variant="outline" className="rounded-md text-[10px]">Haupt</Badge>}
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="uppercase tracking-wider">{typeName(c.kind)}</span>
                  {c.manufacturer && <span>· {c.manufacturer} {c.type || ""}</span>}
                  {c.serial_no && <span className="flex items-center gap-0.5"><Barcode size={11} />{c.serial_no}</span>}
                  {c.baujahr && <span>· BJ {c.baujahr}</span>}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Badge variant="outline" className={`rounded-md text-xs ${COMP_TONE[c.status] || ""}`}>{c.status}</Badge>
                {c.photos?.length > 0 && <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`comp-gallery-${c.id}`} onClick={() => openGallery(c)}><Images size={14} /></Button>}
                <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`comp-inspections-${c.id}`} title="Bestandsprüfungen" onClick={() => setInspComp(c)}><ClipboardText size={14} /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`comp-qr-${c.id}`} onClick={() => setQrComp(c)}><QrCode size={14} /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`comp-edit-${c.id}`} onClick={() => openEdit(c)}><PencilSimple size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {rules && rules.rules.length > 0 && (
        <Card className="mt-6 rounded-md border" data-testid="service-rules-card">
          <div className="flex items-center gap-2 border-b border-border p-4">
            <ClockCountdown size={18} weight="duotone" className="text-primary" />
            <Label2>Fälligkeiten & Wartungsintervalle</Label2>
            {rules.warnings.length > 0 && <Badge variant="outline" className="rounded-md border-red-500/40 bg-red-500/10 text-red-600" data-testid="rules-warning-count">{rules.warnings.length} fällig/überfällig</Badge>}
          </div>
          <div className="divide-y divide-border">
            {rules.rules.map((r) => (
              <div key={r.key} data-testid={`rule-${r.key}`} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    {(r.status === "ueberfaellig" || r.status === "faellig") && <WarningCircle size={15} weight="fill" className="text-red-500" />}
                    {r.label}
                    <Badge variant="outline" className={`rounded-md text-[10px] ${RULE_TONE[r.status] || ""}`}>{RULE_LABEL[r.status] || r.status}</Badge>
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    Intervall {Math.round(r.interval_months / 12 * 10) / 10} J{r.norm ? ` · ${r.norm}` : ""}
                    {r.last_done ? ` · zuletzt ${r.last_done}` : " · noch nicht erfasst"}
                    {r.due_date ? ` · fällig ${r.due_date}` : ""}
                  </div>
                  {r.hinweis && <div className="mt-0.5 text-xs italic text-muted-foreground">{r.hinweis}</div>}
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <Input type="date" className="h-8 w-40 text-sm" data-testid={`rule-date-${r.key}`} value={logInput[r.key] || ""} onChange={(e) => setLogInput({ ...logInput, [r.key]: e.target.value })} />
                  <Button size="sm" variant="outline" className="h-8" data-testid={`rule-save-${r.key}`} onClick={() => saveLog(r.key)}>Erfassen</Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {history && history.length > 0 && (
        <Card className="mt-6 rounded-md border" data-testid="maintenance-history-card">
          <div className="flex items-center gap-2 border-b border-border p-4">
            <ClipboardText size={18} weight="duotone" className="text-primary" />
            <Label2>Wartungshistorie ({history.length})</Label2>
          </div>
          <div className="divide-y divide-border">
            {history.map((h, i) => (
              <button key={i} onClick={() => navigate(`/wartungsauftraege/${h.job_id}`)} data-testid={`history-${i}`}
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-accent">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-medium"><CheckCircle size={15} weight="fill" className="text-emerald-600" />{h.completed_at ? new Date(h.completed_at).toLocaleString("de-DE") : "—"}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{h.completed_by || "—"} · {h.mangel_count + h.sicherheitsmangel_count} Mangel/Mängel · {h.unpruef_count} ungeprüft</div>
                </div>
                {h.health_before != null && <Badge variant="outline" className="rounded-md text-xs">Health {h.health_before}% → {h.health_after}%</Badge>}
              </button>
            ))}
          </div>
        </Card>
      )}

      {defects && defects.length > 0 && (
        <Card className="mt-6 rounded-md border" data-testid="anlage-defects-card">
          <div className="flex items-center gap-2 border-b border-border p-4">
            <WarningCircle size={18} weight="duotone" className="text-primary" />
            <Label2>Offene Mängel & Maßnahmen ({defects.filter((d) => d.status !== "behoben" && d.status !== "abgelehnt").length})</Label2>
          </div>
          <div className="divide-y divide-border">
            {defects.map((d) => (
              <div key={d.id} data-testid={`anlage-defect-${d.id}`} className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    {d.sicherheitsrelevant && <WarningCircle size={15} weight="fill" className="text-red-500" />}
                    {d.bezeichnung}
                    {d.mangelklasse && <span className={`text-[10px] uppercase ${KLASSE_TONE[d.mangelklasse] || ""}`}>{d.mangelklasse}</span>}
                    {d.prioritaet && <span className="text-[10px] uppercase text-muted-foreground">· {d.prioritaet}</span>}
                  </div>
                  {d.massnahme && <div className="mt-0.5 text-xs text-muted-foreground">Maßnahme: {d.massnahme}</div>}
                </div>
                <Badge variant="outline" className={`rounded-md text-xs ${DEFECT_TONE[d.status] || ""}`}>{d.status}</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      {knowledge && knowledge.components.some((c) => c.core) && (
        <Card className="mt-6 rounded-md border" data-testid="anlage-knowledge-card">
          <div className="flex items-center gap-2 border-b border-border p-4">
            <PuzzlePiece size={18} weight="duotone" className="text-primary" />
            <Label2>CMS-Wissen (aus CMS-Core)</Label2>
            <Badge variant="outline" className="rounded-md text-[10px]">{knowledge.offene_zuordnungen} offene Zuordnung(en)</Badge>
          </div>
          <div className="divide-y divide-border">
            {knowledge.components.filter((c) => c.core).map((c) => (
              <div key={c.component_id} data-testid={`anlage-know-${c.component_id}`} className="px-4 py-3">
                <div className="text-sm font-semibold">{c.bezeichnung} <span className="font-mono text-[10px] text-muted-foreground">{c.cms_komponente_key}</span></div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {c.core.maengel.map((m) => <Badge key={m.key} variant="outline" className="rounded-md text-[11px]">{m.name}</Badge>)}
                  {c.core.reparatur_pakete.map((r) => <Badge key={r.key} variant="outline" className="rounded-md border-blue-500/30 text-[11px]">{r.name}</Badge>)}
                  {c.core.normen.map((nm) => <Badge key={nm.key} variant="outline" className="rounded-md text-[11px] text-muted-foreground">{nm.name}</Badge>)}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="component-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Komponente bearbeiten" : "Komponente hinzufügen"}</DialogTitle></DialogHeader>
          <DialogDescription>Zuerst Komponentenart, dann Typ wählen. Technische Felder erscheinen passend zur Art.</DialogDescription>
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div><Label2>Komponentenart</Label2>
                <Select value={form.art_key} onValueChange={setArt}>
                  <SelectTrigger data-testid="comp-art"><SelectValue placeholder="Art wählen" /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label2>Komponententyp</Label2>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger data-testid="comp-kind"><SelectValue placeholder="Typ wählen" /></SelectTrigger>
                  <SelectContent>
                    {compTypes.filter((t) => !form.art_key || t.art_key === form.art_key).map((t) => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Input data-testid="comp-bezeichnung" placeholder="Bezeichnung *" value={form.bezeichnung} onChange={(e) => setForm({ ...form, bezeichnung: e.target.value })} />
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div><Label2>{numLabel}</Label2><Input data-testid="comp-nummer" value={form.komponentennummer || ""} onChange={(e) => setForm({ ...form, komponentennummer: e.target.value })} /></div>
              <div><Label2>Interne Inventar-/Anlagennummer</Label2><Input value={form.inventarnummer || ""} onChange={(e) => setForm({ ...form, inventarnummer: e.target.value })} /></div>
              <div><Label2>Hersteller</Label2><ManufacturerSelect testid="comp-hersteller" value={form.manufacturer || ""} onChange={(v) => setForm({ ...form, manufacturer: v })} /></div>
              <div><Label2>Modell / Baureihe</Label2><CatalogSelect listKey="modellreihe" filterGruppe={form.manufacturer} value={form.modell || ""} onChange={(v) => setForm({ ...form, modell: v })} testid="comp-modell" placeholder="Baureihe wählen …" /></div>
              <div><Label2>Seriennummer</Label2><Input value={form.serial_no || ""} onChange={(e) => setForm({ ...form, serial_no: e.target.value })} /></div>
              <div><Label2>Baujahr</Label2><Input value={form.baujahr || ""} onChange={(e) => setForm({ ...form, baujahr: e.target.value })} /></div>
              <div><Label2>Auftragsnummer</Label2><Input value={form.auftragsnummer || ""} onChange={(e) => setForm({ ...form, auftragsnummer: e.target.value })} /></div>
              <div><Label2>Federtyp links</Label2><CatalogSelect listKey="hoermann_federcode" filterGruppe="Links" value={form.specs?.feder_links || ""} onChange={(v) => setSpec("feder_links", v)} testid="comp-feder-links" placeholder="L…" /></div>
              <div><Label2>Federtyp rechts</Label2><CatalogSelect listKey="hoermann_federcode" filterGruppe="Rechts" value={form.specs?.feder_rechts || ""} onChange={(v) => setSpec("feder_rechts", v)} testid="comp-feder-rechts" placeholder="R…" /></div>
              <div><Label2>Produktnummer</Label2><Input value={form.produktnummer || ""} onChange={(e) => setForm({ ...form, produktnummer: e.target.value })} /></div>
              <div><Label2>Standort im Objekt</Label2><Input value={form.standort_im_objekt || ""} onChange={(e) => setForm({ ...form, standort_im_objekt: e.target.value })} /></div>
              <div><Label2>Betriebsstatus</Label2><Input value={form.betriebsstatus || ""} onChange={(e) => setForm({ ...form, betriebsstatus: e.target.value })} /></div>
              <div><Label2>Einbaudatum</Label2><Input type="date" value={form.einbaudatum || ""} onChange={(e) => setForm({ ...form, einbaudatum: e.target.value })} /></div>
              <div><Label2>Inbetriebnahmedatum</Label2><Input type="date" value={form.inbetriebnahmedatum || ""} onChange={(e) => setForm({ ...form, inbetriebnahmedatum: e.target.value })} /></div>
            </div>
            <div><Label2>Beschreibung</Label2><Input value={form.beschreibung || ""} onChange={(e) => setForm({ ...form, beschreibung: e.target.value })} /></div>
            <div><Label2>Bemerkungen</Label2><Input value={form.bemerkungen || ""} onChange={(e) => setForm({ ...form, bemerkungen: e.target.value })} /></div>

            {form.kind && <OcrPanel artKey={form.art_key} kind={form.kind} onApply={applyOcr} existingPhotos={form.photos || []} />}

            {orderedGroups.map(([gruppe, defs]) => (
              <div key={gruppe} className="rounded-md border border-border p-3" data-testid={`spec-group-${gruppe}`}>
                <Label2>{gruppe}</Label2>
                <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {defs.map((d) => <SpecField key={d.key} def={d} value={form.specs[d.key]} onChange={(v) => setSpec(d.key, v)} optionLists={optionLists} />)}
                </div>
              </div>
            ))}
            {orphanEntries.length > 0 && (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3" data-testid="orphan-specs">
                <div className="text-sm font-semibold text-amber-700">Gespeicherte Werte, die für diesen Typ nicht mehr angezeigt werden</div>
                <p className="mt-0.5 text-xs text-amber-700/80">Diese Werte bleiben erhalten. Zum endgültigen Entfernen einzeln bestätigen.</p>
                <div className="mt-2 space-y-1">
                  {orphanEntries.map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between gap-2 text-sm" data-testid={`orphan-${k}`}>
                      <span className="text-muted-foreground">{k}: <b>{Array.isArray(v) ? v.join(", ") : String(v)}</b></span>
                      <button type="button" data-testid={`orphan-remove-${k}`} className="text-xs text-red-600 hover:underline"
                        onClick={() => { if (window.confirm(`Wert '${k}' endgültig entfernen?`)) setForm((f) => { const s = { ...f.specs }; delete s[k]; return { ...f, specs: s }; }); }}>Entfernen</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter><Button data-testid="comp-save" onClick={save} disabled={!form.bezeichnung || !form.kind}>{editId ? "Speichern" : "Hinzufügen"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!qrComp} onOpenChange={(o) => !o && setQrComp(null)}>
        <DialogContent data-testid="comp-qr-dialog" className="sm:max-w-xs">
          <DialogHeader><DialogTitle className="font-heading">QR-Code · {qrComp?.bezeichnung}</DialogTitle></DialogHeader>
          <DialogDescription>Diesen Code an der Komponente anbringen. Scannen öffnet die Komponente direkt.</DialogDescription>
          <div className="flex flex-col items-center gap-3 py-2">
            {qrComp?.qr_code && <div className="rounded-md bg-white p-3"><QRCodeCanvas value={qrComp.qr_code} size={180} data-testid="comp-qr-canvas" /></div>}
            <div className="font-mono text-sm" data-testid="comp-qr-code">{qrComp?.qr_code || "—"}</div>
            {qrComp?.id && <Button variant="outline" className="gap-2" data-testid="comp-qr-print" onClick={() => setLabelDlg({ open: true, path: `/v1/components/${qrComp.id}/qr-label.pdf` })}><Barcode size={15} /> Etikett drucken (PDF)</Button>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={labelDlg.open} onOpenChange={(o) => setLabelDlg({ ...labelDlg, open: o })}>
        <DialogContent data-testid="label-format-dialog" className="sm:max-w-xs">
          <DialogHeader><DialogTitle className="font-heading">Etiketten-Format</DialogTitle></DialogHeader>
          <DialogDescription>Bogenformat wählen, dann PDF öffnen und drucken.</DialogDescription>
          <Select value={labelFmt} onValueChange={setLabelFmt}>
            <SelectTrigger data-testid="label-format-select"><SelectValue /></SelectTrigger>
            <SelectContent>{labelFormats.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}</SelectContent>
          </Select>
          <DialogFooter className="gap-2 sm:justify-between">
            <Button variant="outline" className="gap-2" data-testid="label-format-print" onClick={() => { printPdf(`${labelDlg.path}?format=${labelFmt}`); setLabelDlg({ open: false, path: "" }); }}><Barcode size={15} /> Direkt drucken</Button>
            <Button data-testid="label-format-open" onClick={() => { openPdf(`${labelDlg.path}?format=${labelFmt}`); setLabelDlg({ open: false, path: "" }); }}>PDF öffnen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!galleryComp} onOpenChange={(o) => !o && setGalleryComp(null)}>
        <DialogContent data-testid="comp-gallery-dialog" className="sm:max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading">Typenschilder · {galleryComp?.bezeichnung}</DialogTitle></DialogHeader>
          <DialogDescription>Verknüpfte Fotos, gefiltert nach Kategorie. Klick öffnet die Großansicht mit Zoom.</DialogDescription>
          {(() => {
            const cats = ["Alle", ...Array.from(new Set(galleryMedia.map((m) => m.category || "Allgemein")))];
            const shown = galleryMedia.filter((m) => galleryFilter === "Alle" || (m.category || "Allgemein") === galleryFilter);
            return (
              <>
                <div className="flex flex-wrap gap-1.5" data-testid="gallery-filters">
                  {cats.map((cat) => <button key={cat} type="button" data-testid={`gallery-filter-${cat}`} onClick={() => setGalleryFilter(cat)} className={`rounded-md border px-2 py-1 text-xs transition-colors ${galleryFilter === cat ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-accent"}`}>{cat}</button>)}
                </div>
                {shown.length === 0
                  ? <div className="py-6 text-center text-sm text-muted-foreground">Keine Fotos in dieser Kategorie.</div>
                  : <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                      {shown.map((m) => (
                        <button key={m.id} type="button" data-testid={`gallery-item-${m.id}`} onClick={() => setLightbox(m)} className="group relative overflow-hidden rounded-md border border-border">
                          <AuthImage mediaId={m.id} alt={m.category} className="h-24 w-full object-cover transition-transform group-hover:scale-105" />
                          <span className="absolute inset-x-0 bottom-0 bg-black/60 px-1 py-0.5 text-[9px] text-white">{m.category || "Allgemein"}</span>
                        </button>
                      ))}
                    </div>}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      <InspectionsDialog component={inspComp} open={!!inspComp} onOpenChange={(o) => !o && setInspComp(null)} />

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent data-testid="gallery-lightbox" className="sm:max-w-3xl">
          <DialogHeader><DialogTitle className="font-heading">{lightbox?.category || "Foto"} · {lightbox?.original_filename}</DialogTitle></DialogHeader>
          {lightbox && <div className="max-h-[70vh] overflow-auto"><AuthImage mediaId={lightbox.id} variant="full" alt={lightbox.category} dataTestid="lightbox-image" className="mx-auto cursor-zoom-in rounded-md" style={{ width: "100%" }} onClick={(e) => { const el = e.currentTarget; el.style.width = el.style.width === "200%" ? "100%" : "200%"; }} /></div>}
          <DialogDescription>Zum Vergrößern/Verkleinern ins Bild klicken.</DialogDescription>
        </DialogContent>
      </Dialog>
    </div>
  );
}
